import React, { useState, useEffect } from 'react';
import { Plus, Trash2 } from 'lucide-react';
import Modal from './Modal';
import { api } from '../lib/api';

export default function TherapyModal({ patient, user, type, id, close, notify, afterChange }) {
    const [form, setForm] = useState({
        therapist: '',
        evaluation_date: new Date().toISOString().slice(0, 10),
        session_type: 'progress',
        diagnosis_medical_necessity: '',
        plan_of_care: '',
        goals: [],
        treatment_sessions: [],
        review_date: '',
        frequency: '',
        duration: '',
        progress_pct: 0,
        progress_note: '',
    });
    const [busy, setBusy] = useState(false);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        async function load() {
            if (!id) {
                setLoading(false);
                return;
            }
            setLoading(true);
            try {
                const r = await api(`/patients/${patient.id}/therapy?type=${type}&id=${id}`);
                const session = r.sessions?.[0];
                if (session) setForm(session);
            } catch (e) { notify(e.message); }
            finally { setLoading(false); }
        }
        load();
    }, [patient.id, type, id, notify]);

    async function save(e) {
        e.preventDefault(); setBusy(true);
        try {
            const body = { ...form };
            await api(`/patients/${patient.id}/therapy${id ? '/' + id : ''}`, {
                method: id ? 'PATCH' : 'POST',
                body
            });
            await afterChange(`${type} session saved.`);
            close();
        } catch (e2) { notify(e2.message); }
        finally { setBusy(false); }
    }

    const addTreatmentSession = () => {
        const newSession = {
            date: new Date().toISOString().slice(0, 10),
            number: (form.treatment_sessions?.length || 0) + 1,
            focus: '',
            response: '',
            pain_level: '',
            progress_pct: 0,
            therapist: form.therapist,
            credentials: type === 'PT' ? 'PT' : 'OTR/L',
            type: 'Individual',
            interventions: '',
            progress: 'Ongoing'
        };
        setForm({ ...form, treatment_sessions: [...(form.treatment_sessions || []), newSession] });
    };

    const updateTreatmentSession = (index, patch) => {
        const updated = [...(form.treatment_sessions || [])];
        updated[index] = { ...updated[index], ...patch };
        setForm({ ...form, treatment_sessions: updated });
    };

    const removeTreatmentSession = (index) => {
        const updated = (form.treatment_sessions || []).filter((_, i) => i !== index);
        setForm({ ...form, treatment_sessions: updated });
    };

    if (loading) return <Modal title="Loading..." onClose={close}><div className="empty-copy">Loading therapy record...</div></Modal>;

    const isPT = type === 'PT';

    return (
        <Modal title={`${isPT ? 'Physical' : 'Occupational'} Therapy Session`} onClose={close} wide>
            <form className="admin-form" onSubmit={save}>
                <div className="form-grid">
                    <label>Therapist<input required value={form.therapist || ''} onChange={e => setForm({ ...form, therapist: e.target.value })}/></label>
                    <label>Evaluation Date<input type="date" required value={form.evaluation_date || ''} onChange={e => setForm({ ...form, evaluation_date: e.target.value })}/></label>
                    <label>Session Type
                        <select required value={form.session_type || 'progress'} onChange={e => setForm({ ...form, session_type: e.target.value })}>
                            <option value="eval">Initial Evaluation</option>
                            <option value="progress">Progress Note</option>
                            <option value="discharge">Discharge Summary</option>
                        </select>
                    </label>
                </div>

                <div className="form-grid">
                    <label>Review Date<input type="date" value={form.review_date || ''} onChange={e => setForm({ ...form, review_date: e.target.value })}/></label>
                    <label>Frequency<input placeholder="e.g. 3x per week" value={form.frequency || ''} onChange={e => setForm({ ...form, frequency: e.target.value })}/></label>
                    <label>Duration<input placeholder="e.g. 8 weeks" value={form.duration || ''} onChange={e => setForm({ ...form, duration: e.target.value })}/></label>
                </div>

                <label>Diagnosis & Medical Necessity<textarea rows={2} value={form.diagnosis_medical_necessity || ''} onChange={e => setForm({ ...form, diagnosis_medical_necessity: e.target.value })}/></label>
                <label>Plan of Care / Treatment Focus<textarea rows={2} value={form.plan_of_care || ''} onChange={e => setForm({ ...form, plan_of_care: e.target.value })}/></label>

                <div className="form-section" style={{ marginTop: 20, border: '1px solid var(--border-color)', padding: 16, borderRadius: 8 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
                        <strong style={{ fontSize: 14 }}>Plan Goals</strong>
                        <button type="button" className="button" style={{ padding: '4px 8px', fontSize: 12 }} onClick={() => {
                            const goal = window.prompt('Enter goal:');
                            if (goal) setForm({ ...form, goals: [...(form.goals || []), goal] });
                        }}><Plus size={14}/> <span>Add Goal</span></button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                        {(form.goals || []).map((g, i) => (
                            <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13 }}>
                                <span style={{ color: 'var(--text-secondary)', width: 20 }}>{i + 1}.</span>
                                <input style={{ flex: 1 }} value={g} onChange={e => {
                                    const updated = [...(form.goals || [])];
                                    updated[i] = e.target.value;
                                    setForm({ ...form, goals: updated });
                                }}/>
                                <button type="button" className="icon-button" onClick={() => {
                                    const updated = (form.goals || []).filter((_, idx) => idx !== i);
                                    setForm({ ...form, goals: updated });
                                }}><Trash2 size={14}/></button>
                            </div>
                        ))}
                    </div>
                </div>

                <div className="form-grid" style={{ marginTop: 20 }}>
                    {isPT ? (
                        <>
                            <label>Pain Score (0-10)<input type="number" min="0" max="10" value={form.pt_pain_score || ''} onChange={e => setForm({ ...form, pt_pain_score: e.target.value })}/></label>
                            <label>Balance Scale<input value={form.pt_balance_scale || ''} onChange={e => setForm({ ...form, pt_balance_scale: e.target.value })}/></label>
                            <label>Gait Status<input value={form.pt_gait_status || ''} onChange={e => setForm({ ...form, pt_gait_status: e.target.value })}/></label>
                        </>
                    ) : (
                        <>
                            <label>ADL Level
                                <select value={form.ot_adl_level || ''} onChange={e => setForm({ ...form, ot_adl_level: e.target.value })}>
                                    <option value="">Select...</option>
                                    <option value="independent">Independent</option>
                                    <option value="assisted">Assisted</option>
                                    <option value="dependent">Dependent</option>
                                    <option value="total">Total</option>
                                </select>
                            </label>
                            <label>Cognition Score<input value={form.ot_cognition_score || ''} onChange={e => setForm({ ...form, ot_cognition_score: e.target.value })}/></label>
                        </>
                    )}
                </div>

                <div className="form-grid">
                    <label>Assistive Devices<input value={form.assistive_devices || ''} onChange={e => setForm({ ...form, assistive_devices: e.target.value })}/></label>
                    <label>Frequency/Duration<input value={form.frequency_duration || ''} onChange={e => setForm({ ...form, frequency_duration: e.target.value })}/></label>
                    <label>Billing Codes (CPT/HCPCS)<input value={form.cpt_hcpcs_codes || ''} onChange={e => setForm({ ...form, cpt_hcpcs_codes: e.target.value })}/></label>
                </div>

                <div style={{ marginTop: 20, borderTop: '1px solid var(--border-color)', paddingTop: 20 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                        <strong style={{ fontSize: 14 }}>Treatment Sessions</strong>
                        <button type="button" className="button primary" style={{ padding: '4px 8px', fontSize: 12 }} onClick={addTreatmentSession}><Plus size={14}/> <span>Add Session</span></button>
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
                        {(form.treatment_sessions || []).map((s, i) => (
                            <div key={i} style={{ border: '1px solid var(--border-color)', padding: 12, borderRadius: 8, backgroundColor: 'var(--bg-secondary)', position: 'relative' }}>
                                <button type="button" className="icon-button" style={{ position: 'absolute', top: 8, right: 8 }} onClick={() => removeTreatmentSession(i)}><Trash2 size={14}/></button>
                                <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                                    <label>Date<input type="date" value={s.date || ''} onChange={e => updateTreatmentSession(i, { date: e.target.value })}/></label>
                                    <label>Session #<input type="number" value={s.number || ''} onChange={e => updateTreatmentSession(i, { number: e.target.value })}/></label>
                                </div>
                                <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                                    <label>{isPT ? 'Treatment Focus' : 'Focus Area'}<input value={isPT ? s.focus : (s.focus || '')} onChange={e => updateTreatmentSession(i, { focus: e.target.value })}/></label>
                                    <label>{isPT ? 'Patient Response' : 'Interventions / Activities'}<input value={isPT ? s.response : (s.interventions || '')} onChange={e => updateTreatmentSession(i, { [isPT ? 'response' : 'interventions']: e.target.value })}/></label>
                                </div>
                                <div className="form-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
                                    {isPT ? (
                                        <>
                                            <label>Pain Level<input value={s.pain_level || ''} onChange={e => updateTreatmentSession(i, { pain_level: e.target.value })}/></label>
                                            <label>Progress %<input type="number" min="0" max="100" value={s.progress_pct || ''} onChange={e => updateTreatmentSession(i, { progress_pct: e.target.value })}/></label>
                                            <label>Therapist<input value={s.therapist || ''} onChange={e => updateTreatmentSession(i, { therapist: e.target.value })}/></label>
                                        </>
                                    ) : (
                                        <>
                                            <label>Type<select value={s.type || 'Individual'} onChange={e => updateTreatmentSession(i, { type: e.target.value })}><option value="Individual">Individual</option><option value="Group">Group</option></select></label>
                                            <label>Progress<select value={s.progress || 'Ongoing'} onChange={e => updateTreatmentSession(i, { progress: e.target.value })}><option value="Improving">Improving</option><option value="Stable">Stable</option><option value="Ongoing">Ongoing</option></select></label>
                                            <label>Therapist<input value={s.therapist || ''} onChange={e => updateTreatmentSession(i, { therapist: e.target.value })}/></label>
                                        </>
                                    )}
                                </div>
                                {!isPT && <label style={{ marginTop: 8 }}>Patient Response<input value={s.response || ''} onChange={e => updateTreatmentSession(i, { response: e.target.value })}/></label>}
                            </div>
                        ))}
                    </div>
                </div>

                <div style={{ marginTop: 24, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                    <label>Overall Progress %<input type="number" min="0" max="100" value={form.progress_pct || ''} onChange={e => setForm({ ...form, progress_pct: e.target.value })}/></label>
                    <label>Progress Note<input value={form.progress_note || ''} onChange={e => setForm({ ...form, progress_note: e.target.value })}/></label>
                </div>

                <label style={{ marginTop: 12 }}>Discharge Summary / Final Notes<textarea rows={3} value={form.discharge_summary || ''} onChange={e => setForm({ ...form, discharge_summary: e.target.value })}/></label>

                <button className="button primary" disabled={busy} style={{ marginTop: 20 }}><span>{busy ? 'Saving...' : 'Save Therapy Record'}</span></button>
            </form>
        </Modal>
    );
}
